import { createEntityAdapter } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { Account, AccountTypeEnum, LoadingStatus, MandateTypeEnum, Portfolio } from '@qvs/core';
import * as fromActions from './account.actions';
import { onEntityLoadingStatus } from '~/shared/helpers/loading-status-helper';
import { selectPortfolioCurrent, selectPortfolios } from '~/modules/portfolio/slice';
import { selectMandateTypeEntities } from '~/modules/mandateTypes/slice';

const accountEntityAdapter = createEntityAdapter<Account>();

export const initialState = accountEntityAdapter.getInitialState({
  loadingStatus: LoadingStatus.IDLE
});

export type State = typeof initialState;

export const accountFeatureKey = 'accounts';

const reducer = createReducer(
  initialState,
  ...onEntityLoadingStatus<State>(fromActions.loadAccounts, fromActions.loadAccountsSuccess, fromActions.loadAccountsFailure),
  on(fromActions.upsertAccounts, (state, { entities }) => accountEntityAdapter.upsertMany(entities, state))
);

export const accountFeature = createFeature({
  name: accountFeatureKey,
  reducer
});

const { selectAccountsState } = accountFeature;
const selectLoadingStatus = createSelector(selectAccountsState, (state) => state.loadingStatus);

const selectAllAccounts = createSelector(selectAccountsState, accountEntityAdapter.getSelectors().selectAll);

export const findPortfolioForAccount = (portfolios: Portfolio[], account: Account) =>
  portfolios.find((portfolio) => portfolio.portfolioNumber === account.portfolioId);

export const selectAllAccountsWithPortfolio = createSelector(selectAllAccounts, selectPortfolios, (accounts, portfolios) =>
  accounts.map((account) => ({
    ...account,
    portfolio: findPortfolioForAccount(portfolios, account)
  }))
);

export const selectIsLoadingAccounts = createSelector(
  selectLoadingStatus,
  (loadingStatus) => loadingStatus === LoadingStatus.LOADING
);

export const selectAccountsThatCanCreatePayments = createSelector(selectAllAccountsWithPortfolio, (accounts) =>
  accounts.filter((account) => !!account.authorizedPaymentsAndTransfers)
);

export const selectAccountsThatCanCreatePaymentsIdMap = createSelector(selectAccountsThatCanCreatePayments, (accounts) => {
  const map = {};
  accounts.forEach((account) => (map[account.id] = account));
  return map;
});

export const selectAccountsThatAllowTrading = createSelector(selectAllAccountsWithPortfolio, (accounts) =>
  accounts.filter((account) => account.authorizedStockExchangeOrders === true && account.type === AccountTypeEnum.CURRENT_ACCOUNT)
);

export const selectAccountById = (accountId: string) =>
  createSelector(selectAllAccountsWithPortfolio, (accounts) => accounts.find((account) => account.id === accountId));

export const selectPortfoliosWhichAllowTrading = createSelector(
  selectAccountsThatAllowTrading,
  selectMandateTypeEntities,
  (accounts, mandateTypes) =>
    [...new Set(accounts.map((account) => account.portfolio))]
      .filter((portfolio) => !!portfolio)
      .filter(
        (portfolio) =>
          MandateTypeEnum.DISCRETIONARY !== mandateTypes[portfolio.portfolioMandateTypeId]?.mandateTypeEnum &&
          portfolio.portfolioMandateTypeId !== undefined
      )
);

export const selectCurrentPortfolioAllowsTrading = createSelector(
  selectPortfolioCurrent,
  selectPortfoliosWhichAllowTrading,
  (currentPortfolio, portfolios) => portfolios.some((portfolio) => portfolio.portfolioNumber === currentPortfolio.portfolioNumber)
);

export const selectAccountFromCurrentPortfolioThatAllowTrading = createSelector(
  selectPortfolioCurrent,
  selectAccountsThatAllowTrading,
  (currentPortfolio, accounts) =>
    accounts.filter((account) => account.portfolio.portfolioNumber === currentPortfolio.portfolioNumber)
);
